import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Container, Button } from 'react-bootstrap';

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false, errorId: null };
	}

	static getDerivedStateFromError() {
		// Generate unique error ID for logging correlation
		const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

		return {
			hasError: true,
			errorId
		};
	}

	componentDidCatch(error, errorInfo) {
		// Log error securely (without sensitive data)
		const sanitizedError = {
			message: error.message.replace(/password|token|secret|key/gi, '[REDACTED]'),
			stack: error.stack?.replace(/password|token|secret|key/gi, '[REDACTED]'),
			component: errorInfo.componentStack,
			errorId: this.state.errorId,
			timestamp: new Date().toISOString(),
			userAgent: navigator.userAgent,
			url: window.location.href
		};

		// Send to logging service (implement based on your logging infrastructure)
		this.logError(sanitizedError);
	}

	logError = (errorDetails) => {
		// Implementation depends on your logging service
		if (import.meta.env.DEV) {
			console.error('Application Error:', errorDetails);
		} else {
			// Send to production logging service
			// Example: Sentry, LogRocket, etc.
		}
	};

	handleReload = () => {
		window.location.reload();
	};

	render() {
		if (this.state.hasError) {
			return (
				<Container className="mt-5">
					<Alert variant="danger">
						<Alert.Heading>Something went wrong</Alert.Heading>						<p>
							We&apos;re sorry, but an unexpected error occurred.
							Our team has been notified and is working to resolve the issue.
						</p>
						{import.meta.env.DEV && (
							<p className="small text-muted">
								Error ID: {this.state.errorId}
							</p>
						)}
						<hr />
						<div className="d-flex justify-content-end">
							<Button variant="outline-danger" onClick={this.handleReload}>
								Reload Page
							</Button>
						</div>
					</Alert>
				</Container>
			);
		}

		return this.props.children;
	}
}

ErrorBoundary.propTypes = {
	children: PropTypes.node.isRequired
};

export default ErrorBoundary;
